import { Text, Flex, CheckboxGroup, Switch, Button, useToast } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { InputField } from '@/components/InputField';
import { useAccessControlPermissionTypeById, useUpdatePermissionTypeMutation } from '../../services/queries/access-control';
import { accessControlPermissionTypesEditBreadcrumb } from './breadcrumb';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { accessControlPermissionTypeEditSchema, AccessControlPermissionTypeEditFormData } from './schemas';
import { Permission } from '@/model';
import { useEffect } from 'react';
import { queryClient } from '@/services/api/queryClient';
import { useTranslation } from 'react-i18next';

export const AccessControlPermissionTypeEdit = () => {
  const { id } = useParams();
  const toast = useToast();
  const { t } = useTranslation();
  const { data: accessControlPermission, isLoading: isLoadingAccessControlPermission } = useAccessControlPermissionTypeById(
    id as string,
  );
  const { mutate: updateAccessControlPermissionType, isLoading: isLoadingCreateAccessControlPermissionType } =
    useUpdatePermissionTypeMutation();

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AccessControlPermissionTypeEditFormData>({
    defaultValues: {
      name: '',
      permissions: {
        ACCESS_CONTROL: false,
        ACCESS_PLANS: false,
        APPLICATION: false,
        COMMUNICATION_CENTRAL: false,
        FINANCIAL: false,
        HOME: false,
        PARTNERS: false,
        SUBSCRIBERS: false,
        USERS: false,
      },
    },
    resolver: zodResolver(accessControlPermissionTypeEditSchema),
  });

  const permissionsValues = watch('permissions');

  const handlePermissionChange = (permission: Permission, value: boolean) => {
    setValue(`permissions.${permission}`, value as never);
  };

  const onSubmit = (data: AccessControlPermissionTypeEditFormData) => {
    updateAccessControlPermissionType(
      {
        id: id as string,
        payload: data,
      },
      {
        onSuccess: async () => {
          toast({
            title: t('pages.access_control_permission_type_edit.toast.success'),
            status: 'success',
          });

          await queryClient.invalidateQueries(['GET_ACCESS_CONTROL_PERMISSION_TYPES']);
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.access_control_permission_type_edit.toast.error'),
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!accessControlPermission) return;

    setValue('name', accessControlPermission.name);
    setValue('permissions', accessControlPermission.permissions);
  }, [accessControlPermission, setValue]);

  return (
    <PageContent
      title={t('pages.access_control_permission_type_edit.title')}
      breadcrumbItems={accessControlPermissionTypesEditBreadcrumb}
    >
      <SectionBox bodyProps={{ gap: 'small' }} mt={8}>
        <Flex direction='column' gap='deka'>
          <Text fontWeight='medium' fontSize='18px'>
            {t('pages.access_control_permission_type_edit.form.section_title')}
          </Text>
          <Flex as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap='micro'>
            <Flex>
              <InputField
                withoutFloating
                label={t('pages.access_control_permission_type_edit.form.label_name')}
                {...register('name')}
                maxW={{ md: '300px' }}
                rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
              />
              <Button minW='fit-content' type='submit' display={{ base: 'none', md: 'initial' }}>
                {t('pages.access_control_permission_type_edit.form.button_submit_desktop')}
              </Button>
            </Flex>

            <Flex direction='column' gap={4} alignItems='flex-start'>
              <Text fontWeight='medium' fontSize='18px' mt={6} mb={2}>
                {t('pages.access_control_permission_type_edit.form.permissions_section_title')}
              </Text>
              <CheckboxGroup>
                <Switch value={Permission.HOME} onChange={event => handlePermissionChange(Permission.HOME, event.target.checked)}>
                  {t('pages.access_control_permission_type_edit.permissions_labels.HOME')}
                </Switch>
                <Switch
                  value={Permission.USERS}
                  isChecked={permissionsValues.USERS}
                  onChange={event => handlePermissionChange(Permission.USERS, event.target.checked)}
                >
                  {t('pages.access_control_permission_type_edit.permissions_labels.USERS')}
                </Switch>
                <Switch
                  value={Permission.SUBSCRIBERS}
                  isChecked={permissionsValues.SUBSCRIBERS}
                  onChange={event => handlePermissionChange(Permission.SUBSCRIBERS, event.target.checked)}
                >
                  {t('pages.access_control_permission_type_edit.permissions_labels.SUBSCRIBERS')}
                </Switch>
                <Switch
                  value={Permission.ACCESS_PLANS}
                  isChecked={permissionsValues.ACCESS_PLANS}
                  onChange={event => handlePermissionChange(Permission.ACCESS_PLANS, event.target.checked)}
                >
                  {t('pages.access_control_permission_type_edit.permissions_labels.ACCESS_PLANS')}
                </Switch>
                <Switch
                  value={Permission.APPLICATION}
                  isChecked={permissionsValues.APPLICATION}
                  onChange={event => handlePermissionChange(Permission.APPLICATION, event.target.checked)}
                >
                  {t('pages.access_control_permission_type_edit.permissions_labels.APPLICATION')}
                </Switch>
                <Switch
                  value={Permission.FINANCIAL}
                  isChecked={permissionsValues.FINANCIAL}
                  onChange={event => handlePermissionChange(Permission.FINANCIAL, event.target.checked)}
                >
                  {t('pages.access_control_permission_type_edit.permissions_labels.FINANCIAL')}
                </Switch>
                <Switch
                  value={Permission.PARTNERS}
                  isChecked={permissionsValues.PARTNERS}
                  onChange={event => handlePermissionChange(Permission.PARTNERS, event.target.checked)}
                >
                  {t('pages.access_control_permission_type_edit.permissions_labels.PARTNERS')}
                </Switch>
                <Switch
                  value={Permission.ACCESS_CONTROL}
                  isChecked={permissionsValues.ACCESS_CONTROL}
                  onChange={event => handlePermissionChange(Permission.ACCESS_CONTROL, event.target.checked)}
                >
                  {t('pages.access_control_permission_type_edit.permissions_labels.ACCESS_CONTROL')}
                </Switch>
                <Switch
                  value={Permission.COMMUNICATION_CENTRAL}
                  isChecked={permissionsValues.COMMUNICATION_CENTRAL}
                  onChange={event => handlePermissionChange(Permission.COMMUNICATION_CENTRAL, event.target.checked)}
                >
                  {t('pages.access_control_permission_type_edit.permissions_labels.COMMUNICATION_CENTRAL')}
                </Switch>
              </CheckboxGroup>
              {errors?.permissions ? <Text color='red.500'>{errors?.permissions?.message}</Text> : null}
            </Flex>

            <Button type='submit' display={{ md: 'none' }} mt={8}>
              {t('pages.access_control_permission_type_edit.form.button_submit_mobile')}
            </Button>
          </Flex>
        </Flex>
      </SectionBox>

      <CenteredSpinner isLoading={isLoadingAccessControlPermission || isLoadingCreateAccessControlPermissionType} />
    </PageContent>
  );
};
